"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getApiMePermissions } from "@/lib/api-client/endpoints/me/me";
import {
  useBooleanFeatureFlag,
  useFeatureFlagOverride,
} from "@/lib/feature-flags/FeatureFlags";
import { FEATURE_FLAG_KEYS } from "@/lib/feature-flags/flags";
import { useLaunchDarklyIdentityStatus } from "@/lib/feature-flags/LaunchDarklyIdentityBridge";
import { reportError } from "@/lib/observability/reportError";
import {
  useSignalRConnection,
  useSignalRConnectionState,
} from "@/lib/signalr/SignalRContext";
import { SIGNALR_EVENTS, SIGNALR_METHODS } from "@/lib/signalr/events";
import { useSignalREvent } from "@/lib/signalr/useSignalREvent";
import { useClaritySession } from "./auth-store";
import {
  getSessionIdentityKey,
  usesTokenPermissionsOnly,
} from "./session-identity";
import {
  SessionPermissionsContext,
  type SessionPermissionsState,
} from "./session-permissions-context";
import {
  getPermissionVersion,
  startPermissionSync,
  type PermissionSnapshot,
  type PermissionSync,
} from "./session-permissions-sync";

const NO_PERMISSIONS: string[] = [];
const LOADING: SessionPermissionsState = {
  permissions: NO_PERMISSIONS,
  isLoading: true,
};
const SIGNED_OUT: SessionPermissionsState = {
  permissions: NO_PERMISSIONS,
  isLoading: false,
};

type SyncedSnapshot = {
  key: string;
  snapshot: PermissionSnapshot | null;
};

/**
 * Shares one permission source with every `usePermissions` consumer: JWT
 * permissions while SessionPermissionsFromBackend is off, the server snapshot
 * while it is on. Switching sources never remounts children or rebuilds the hub.
 * Tenant access and `actor_type` stay on the JWT either way.
 */
export function SessionPermissionsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const session = useClaritySession();
  const flagValue = useBooleanFeatureFlag(
    FEATURE_FLAG_KEYS.sessionPermissionsFromBackend,
  );
  const flagOverride = useFeatureFlagOverride(
    FEATURE_FLAG_KEYS.sessionPermissionsFromBackend,
  );
  const launchDarkly = useLaunchDarklyIdentityStatus();
  // The server source requires a live flag evaluated for this user. When
  // LaunchDarkly is unavailable, stay on JWT permissions, even if a cached
  // flag value says otherwise. Explicit overrides (E2E) are deterministic.
  const flagsReady = flagOverride !== undefined || launchDarkly !== "pending";
  const flagEnabled =
    flagOverride ?? (launchDarkly === "identified" && flagValue);
  const connection = useSignalRConnection();
  const isConnected = useSignalRConnectionState();

  const status = session.status;
  const accessToken = status === "authenticated" ? session.accessToken : null;
  const claims = status === "authenticated" ? session.claims : null;
  // Kept separately so rollback is immediate and token refreshes never
  // overwrite the server snapshot.
  const jwtPermissions =
    status === "authenticated" ? session.permissions : NO_PERMISSIONS;
  const identity = claims ? getSessionIdentityKey(claims) : null;
  const tokenOnly = useMemo(
    () =>
      accessToken !== null &&
      claims !== null &&
      usesTokenPermissionsOnly(accessToken, claims),
    [accessToken, claims],
  );
  // A server snapshot belongs to one user while the flag is on; any other key
  // discards it.
  const syncKey =
    identity !== null && flagsReady && flagEnabled && !tokenOnly
      ? identity
      : null;

  const [synced, setSynced] = useState<SyncedSnapshot | null>(null);
  const syncRef = useRef<PermissionSync | null>(null);
  const hubRef = useRef({ connection, isConnected });

  useEffect(() => {
    hubRef.current = { connection, isConnected };
  }, [connection, isConnected]);

  useEffect(() => {
    if (syncKey === null) return;

    const sync = startPermissionSync({
      fetchSnapshot: async (signal) =>
        (await getApiMePermissions({ signal })).data,
      getHandshake: () => {
        const hub = hubRef.current.connection;
        if (!hub || !hubRef.current.isConnected) return null;

        return (version) =>
          hub.invoke(SIGNALR_METHODS.PERMISSIONS_VERSION, version);
      },
      onChange: (snapshot) => setSynced({ key: syncKey, snapshot }),
      onError: (error) => reportError(error, { source: "session-permissions" }),
    });
    syncRef.current = sync;

    return () => {
      sync.stop();
      syncRef.current = null;
      setSynced(null);
    };
  }, [syncKey]);

  useSignalREvent<unknown>(SIGNALR_EVENTS.PERMISSIONS_CHANGED, (payload) => {
    const version = getPermissionVersion(payload);
    if (version !== undefined) syncRef.current?.request("api", version);
  });

  // Every (re)connect confirms the checksum; a match costs no HTTP request.
  useEffect(() => {
    if (syncKey !== null && isConnected) {
      syncRef.current?.request("handshake");
    }
  }, [isConnected, syncKey]);

  const value = useMemo<SessionPermissionsState>(() => {
    if (status === "loading") return LOADING;
    if (status !== "authenticated") return SIGNED_OUT;
    // The source is unknown until flags reflect this user.
    if (!flagsReady) return LOADING;
    if (syncKey === null) {
      return { permissions: jwtPermissions, isLoading: false };
    }
    // No JWT fallback while the flag is on: failures deny access.
    if (synced?.key !== syncKey) return LOADING;

    return {
      permissions: synced.snapshot?.permissions ?? NO_PERMISSIONS,
      isLoading: false,
    };
  }, [flagsReady, jwtPermissions, status, syncKey, synced]);

  return (
    <SessionPermissionsContext.Provider value={value}>
      {children}
    </SessionPermissionsContext.Provider>
  );
}
